import { deployedBuildQueryOptions, useAuth, usePrivateChannel } from '@/lib'
import { logger } from '@/ui'
import { broadcastChannel, releaseEvent } from '@jobilla/nexus-sdk'
import { useQuery } from '@tanstack/react-query'
import type { AnyRouter } from '@tanstack/react-router'
import { RotateCwIcon } from 'lucide-react'
import { motion } from 'motion/react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

const CHECK_INTERVAL_MS = 5 * 60 * 1000
const PROMPT_DELAY_MS = 15 * 60 * 1000
const PENDING_RELOAD_STORAGE_KEY = 'app-version:pending-reload'

type DetectionSource = 'check' | 'announcement'
type PromptSource = 'timer' | 'announcement'
type ReloadMethod = 'navigation' | 'prompt'

const pendingReloadSchema = z.object({
	fromBuiltAt: z.string(),
	method: z.enum(['navigation', 'prompt']),
	outdatedSince: z.number().nullable(),
})

function minutesSince(timestamp: number | null): number | null {
	return timestamp === null ? null : Math.round((Date.now() - timestamp) / 60_000)
}

function reloadToNewVersion(
	runningBuiltAt: string | undefined,
	method: ReloadMethod,
	outdatedSince: number | null,
): void {
	if (runningBuiltAt) {
		try {
			sessionStorage.setItem(
				PENDING_RELOAD_STORAGE_KEY,
				JSON.stringify({ fromBuiltAt: runningBuiltAt, method, outdatedSince }),
			)
		} catch {
			// storage unavailable
		}
	}

	window.location.reload()
}

function trackPendingReload(runningBuiltAt: string | undefined): void {
	let stored: unknown = null
	try {
		const raw = sessionStorage.getItem(PENDING_RELOAD_STORAGE_KEY)
		sessionStorage.removeItem(PENDING_RELOAD_STORAGE_KEY)
		stored = raw ? JSON.parse(raw) : null
	} catch {
		return
	}

	const pendingReload = pendingReloadSchema.safeParse(stored)
	if (!pendingReload.success) {
		return
	}

	const { fromBuiltAt, method, outdatedSince } = pendingReload.data
	logger.track(method === 'navigation' ? 'App Version: Auto-Resolved' : 'App Version: Reload Prompt Used', {
		data: {
			from_built_at: fromBuiltAt,
			to_built_at: runningBuiltAt ?? null,
			reached_new_version: !!runningBuiltAt && runningBuiltAt !== fromBuiltAt,
			minutes_outdated: minutesSince(outdatedSince),
		},
	})
}

export function AppVersionCheck({ router }: { router: AnyRouter }) {
	const { t } = useTranslation('common')
	const [staleSince, setStaleSince] = useState<number | null>(null)
	const staleSinceRef = useRef(staleSince)
	staleSinceRef.current = staleSince
	const [promptSource, setPromptSource] = useState<PromptSource | null>(null)
	const detectionSourceRef = useRef<DetectionSource>('check')
	const { user } = useAuth()
	const runningBuiltAt = import.meta.env.VITE_APP_BUILT_AT

	const { data: deployedBuiltAt, refetch } = useQuery({
		...deployedBuildQueryOptions,
		enabled: !!runningBuiltAt && !import.meta.env.DEV && import.meta.env.VITE_E2E !== 'true',
		refetchInterval: CHECK_INTERVAL_MS,
		refetchOnWindowFocus: true,
	})

	const newBuiltAt = deployedBuiltAt && deployedBuiltAt !== runningBuiltAt ? deployedBuiltAt : null

	usePrivateChannel(user && runningBuiltAt ? broadcastChannel.AppReleases : null, {
		[releaseEvent.Announced]: async () => {
			detectionSourceRef.current = 'announcement'
			const { data: latestBuiltAt } = await refetch()

			if (latestBuiltAt && latestBuiltAt !== runningBuiltAt) {
				setPromptSource('announcement')
			}
		},
	})

	useEffect(() => {
		if (runningBuiltAt) {
			logger.register({ app_built_at: runningBuiltAt })
		}

		trackPendingReload(runningBuiltAt)
	}, [runningBuiltAt])

	useEffect(() => {
		const onPageShow = (event: PageTransitionEvent) => {
			if (event.persisted) {
				void refetch()
			}
		}

		window.addEventListener('pageshow', onPageShow)
		return () => window.removeEventListener('pageshow', onPageShow)
	}, [refetch])

	useEffect(() => {
		if (!newBuiltAt) {
			setStaleSince(null)
			setPromptSource(null)
			return
		}

		if (staleSince === null) {
			setStaleSince(Date.now())
			logger.track('App Version: Outdated', {
				data: {
					source: detectionSourceRef.current,
					running_built_at: runningBuiltAt ?? null,
					deployed_built_at: newBuiltAt,
				},
			})
			detectionSourceRef.current = 'check'
		}
	}, [newBuiltAt, staleSince, runningBuiltAt])

	useEffect(() => {
		if (!newBuiltAt) {
			return
		}

		// Emitted after navigation blockers have run and the URL has changed.
		return router.subscribe('onBeforeNavigate', (event) => {
			if (event.fromLocation && event.pathChanged) {
				reloadToNewVersion(runningBuiltAt, 'navigation', staleSinceRef.current)
			}
		})
	}, [router, newBuiltAt, runningBuiltAt])

	useEffect(() => {
		if (!newBuiltAt || staleSince === null) {
			return
		}

		const timeout = window.setTimeout(
			() => setPromptSource((current) => current ?? 'timer'),
			Math.max(0, staleSince + PROMPT_DELAY_MS - Date.now()),
		)

		return () => window.clearTimeout(timeout)
	}, [newBuiltAt, staleSince])

	useEffect(() => {
		if (!promptSource) {
			return
		}

		logger.track('App Version: Reload Prompt Shown', {
			data: {
				source: promptSource,
				minutes_outdated: minutesSince(staleSinceRef.current),
			},
		})
	}, [promptSource])

	if (!promptSource) {
		return null
	}

	return (
		<motion.div
			initial={{ opacity: 0, y: 8 }}
			animate={{ opacity: 1, y: 0 }}
			transition={{ duration: 0.2, ease: 'easeOut' }}
			className="pointer-events-none fixed inset-x-0 bottom-4 z-[100] flex justify-center px-4 [body:has([data-mode-banner-row]>*)_&]:bottom-10"
		>
			<div
				role="status"
				className="pointer-events-auto flex items-center gap-3 rounded-full bg-grey-900 py-1.5 pl-4 pr-1.5 text-sm font-medium text-white shadow-lg"
			>
				<RotateCwIcon className="size-3.5 shrink-0" aria-hidden="true" />
				<span>{t('common.newVersionTitle')}</span>
				<button
					type="button"
					onClick={() => reloadToNewVersion(runningBuiltAt, 'prompt', staleSince)}
					className="cursor-pointer rounded-full bg-white/15 px-3 py-1 font-semibold hover:bg-white/25"
				>
					{t('common.reload')}
				</button>
			</div>
		</motion.div>
	)
}
